import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import {
  getOrgPlayersPerDay,
  getOrgReportData,
  PAGE_SIZE,
  getInstructorReportData,
  getCompareGamesData,
  getSponsoredReportData,
  getSponsoredReportGameData,
  getSponsoredPlayersPerDay,
  SPONSORED_PAGE_SIZE,
  getPlayerReportData,
} from '../api/reportingApiService'
import { PaginationSelectOption } from '../composites/GamesOverview/components/PaginationControls'
import { noop } from '../util/functional'
import { useUser } from './userContext'
import {
  CompareGameData,
  GenericReportPageData,
  InstructorReportSummaryData,
  OrgReportSummaryData,
  parseCompareGameApiData,
  parseInstructorSummaryApiData,
  parseOrgSummaryApiData,
  parsePlayerReportApiData,
  parseSponsoredReportData,
  parseSponsoredReportGameData,
  PlayerReportData,
  ReportType,
  SponsoredReportData,
  SponsoredReportGame,
} from './ReportsContextHelper'
import { DailyPlayers } from '../api/reportingTypes'
import { NOW, YEAR } from '../pages/reports/components/ReportHelper'

export const PAGE_SIZE_OPTIONS = [
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 50, label: '50' },
]

export type ReportFilters = {
  createdAtFrom?: string
  createdAtUntil?: string
  tag?: number
  playerEmail?: string
  gameName?: string
}

export enum ReportSortBy {
  NAME = 'user_name',
  GAMES_CREATED = 'number_of_games_created',
  GAMES_CO_OWNED = 'number_of_games_co_owned',
  GAMES_PLAYED = 'number_of_games_played',
  GAMES_CO_PLAYED = 'number_of_games_co_played',
  PLAYERS_IN_GAMES = 'number_of_players_in_games',
  IR_GAME_NAME = 'name',
  IR_CREATED_AT = 'created_at_raw',
  IR_LAST_ANSWER_RECEIVED = 'last_answer_received_raw',
  IR_STATUS = 'open_status_sorter',
  IR_PLAYERS = 'number_of_teams',
  SP_EMAIL = 'email',
  SP_CREATED = 'createdAt',
  SP_ORGANISATION = 'organisation',
}

export enum ReportSortDir {
  ASC = ' asc',
  DESC = ' desc',
}

export interface ReportsDataProviderInterface {
  playerReportData?: PlayerReportData
  orgSummaryData?: OrgReportSummaryData
  orgDailyPlayers?: DailyPlayers
  instructorReportSummaryData?: InstructorReportSummaryData
  compareGameData?: CompareGameData[]
  sponsoredReportData?: SponsoredReportData
  sponsoredDailyPlayers?: DailyPlayers
  currentPage: number
  pageSize: number
  pageData: GenericReportPageData[] | undefined
  loading: boolean
  lastPage: number
  allPageOptions: PaginationSelectOption[]
  selectPageSize: (newPageSize: number) => void
  prev: () => void
  next: () => void
  setPage: (newPage: number) => void
  refreshData: () => void
  sortBy?: ReportSortBy
  sortDir?: ReportSortDir
  setSort?: (sortBy: ReportSortBy, sortDir: ReportSortDir) => void
  filters?: ReportFilters
  setFilters: (filters: ReportFilters) => void
  initialFetchDone?: boolean
  fetchCompareGamesData?: (gameIds: number[]) => void
  fetchSponsoredGameData?: (
    gameId: number,
    fromDate?: string,
    untilDate?: string,
  ) => Promise<SponsoredReportGame | undefined>
}

const ReporsDataContext = createContext<ReportsDataProviderInterface>({
  playerReportData: undefined,
  orgSummaryData: undefined,
  orgDailyPlayers: undefined,
  instructorReportSummaryData: undefined,
  compareGameData: undefined,
  sponsoredReportData: undefined,
  sponsoredDailyPlayers: undefined,
  currentPage: 1,
  pageSize: PAGE_SIZE,
  pageData: undefined,
  loading: true,
  // TODO: we need a total number of items to determine the last page so we can disable paginating after that point
  lastPage: 1,
  allPageOptions: [] as PaginationSelectOption[],
  selectPageSize: noop,
  prev: noop,
  next: noop,
  setPage: noop,
  refreshData: noop,
  sortBy: ReportSortBy.NAME,
  sortDir: ReportSortDir.ASC,
  setSort: noop,
  setFilters: (filters: ReportFilters) => {},
})

interface Props {
  reportType: ReportType
  children?: React.ReactNode
}

export const ReportsDataProvider: React.FC<Props> = ({ reportType = ReportType.ORG, children }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [playerReportData, setPlayerReportData] = useState<PlayerReportData>()
  const [orgSummaryData, setOrgSummaryData] = useState<OrgReportSummaryData>()
  const [orgDailyPlayers, setOrgDailyPlayers] = useState<DailyPlayers>()
  const [sponsoredDailyPlayers, setSponsoredDailyPlayers] = useState<DailyPlayers>()
  const [instructorReportSummaryData, setInstructorReportSummaryData] = useState<InstructorReportSummaryData>()
  const [compareGameData, setCompareGameData] = useState<CompareGameData[]>()
  const [sponsoredReportData, setSponsoredReportData] = useState<SponsoredReportData>()
  const [pageData, setPageData] = useState<GenericReportPageData[]>()
  const [pageSize, setPageSize] = useState<number>(
    reportType === ReportType.SPONSORED ? SPONSORED_PAGE_SIZE : PAGE_SIZE,
  )
  const [loading, setLoading] = useState(true)
  const [lastPage, setLastPage] = useState<number>(1)
  const [allPageOptions, setAllPageOptions] = useState<PaginationSelectOption[]>([{ value: 1, label: '1' }])
  const [sortBy, setSortBy] = useState<ReportSortBy>(
    reportType === ReportType.SPONSORED ? ReportSortBy.SP_CREATED : ReportSortBy.NAME,
  )
  const [sortDir, setSortDir] = useState<ReportSortDir>(
    reportType === ReportType.SPONSORED ? ReportSortDir.DESC : ReportSortDir.ASC,
  )
  const [activeSponsoredGameId, setActiveSponsoredGameId] = useState<number>()

  const { user } = useUser()
  const [filters, setFilters] = useState<ReportFilters>(
    reportType === ReportType.INSTRUCTOR ? { createdAtFrom: new Date(NOW() - YEAR).toISOString().split('T')[0] } : {},
  )
  const [initialFetchDone, setInitialFetchDone] = useState<boolean>(false)
  const [refreshCounter, setRefreshCounter] = useState(0)

  const next = useCallback(() => {
    if (loading) {
      return
    }

    setCurrentPage((currentState) => currentState + 1)
  }, [loading])

  const prev = useCallback(() => {
    if (loading) {
      return
    }

    setCurrentPage((currentState) => currentState - 1)
  }, [loading])

  const selectPageSize = useCallback(
    (newPageSize: number) => {
      if (loading) {
        return
      }

      setCurrentPage(1)
      setPageSize(newPageSize)
    },
    [loading],
  )

  const setPage = useCallback(
    (newPage: number) => {
      if (loading) {
        return
      }

      setCurrentPage(newPage)
    },
    [loading],
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [filters])

  //THIS IS THE ORGANIZATION REPORT FETCHER
  useEffect(() => {
    if (!user || reportType !== ReportType.ORG) {
      return
    }
    setLoading(true)
    getOrgReportData({
      perPage: pageSize,
      page: currentPage,
      businessId: user?.activeBusiness.id,
      sortBy: sortBy + sortDir,
      fromDate: filters.createdAtFrom,
      untilDate: filters.createdAtUntil,
    })
      .then((reportDataResponse) => {
        if (reportDataResponse.success) {
          setLastPage(Math.ceil(reportDataResponse.value.userCollectionCount / pageSize))
          if (reportType === ReportType.ORG) {
            const { usersTable, summaryData } = parseOrgSummaryApiData(reportDataResponse.value)
            setPageData(usersTable)
            setOrgSummaryData(summaryData)
          }
        } else {
          //notifyFetchingGamesFailed()
          console.error(reportDataResponse.error)
        }
      })
      .finally(() => {
        setLoading(false)
        setInitialFetchDone(true)
      })
    getOrgPlayersPerDay({ businessId: user?.activeBusiness.id }).then((dailyResponse) => {
      if (dailyResponse.success) {
        setOrgDailyPlayers(dailyResponse.value.playersPerDays)
      } else {
        console.error(dailyResponse.error)
      }
    })
  }, [
    currentPage,
    filters.createdAtFrom,
    filters.createdAtUntil,
    pageSize,
    reportType,
    sortBy,
    sortDir,
    user,
    refreshCounter,
  ])

  //THIS IS THE INSTRUCTOR REPORT FETCHER
  useEffect(() => {
    if (!user || reportType !== ReportType.INSTRUCTOR) {
      return
    }
    setLoading(true)
    getInstructorReportData({
      businessId: user?.activeBusiness.id,
      perPage: pageSize,
      page: currentPage,
      sortBy: sortBy + sortDir,
      fromDate: filters.createdAtFrom,
      untilDate: filters.createdAtUntil,
    })
      .then((reportDataResponse) => {
        if (reportDataResponse.success) {
          setLastPage(Math.ceil(reportDataResponse.value.gamesCollectionCount / pageSize))
          if (reportType === ReportType.INSTRUCTOR) {
            const { gamesTable, summaryData } = parseInstructorSummaryApiData(reportDataResponse.value)
            setPageData(gamesTable)
            setInstructorReportSummaryData(summaryData)
          }
        } else {
          //notifyFetchingGamesFailed()
          console.error(reportDataResponse.error)
        }
      })
      .finally(() => {
        setLoading(false)
        setInitialFetchDone(true)
      })
  }, [
    currentPage,
    filters.createdAtFrom,
    filters.createdAtUntil,
    pageSize,
    reportType,
    sortBy,
    sortDir,
    user,
    refreshCounter,
  ])

  //THIS IS FOR THE PLAYER REPORT
  useEffect(() => {
    if (!user || reportType !== ReportType.PLAYER) {
      return
    }
    setLoading(true)
    getPlayerReportData({
      fromDate: filters.createdAtFrom,
      untilDate: filters.createdAtUntil,
      gameName: filters.gameName,
      playerEmail: filters.playerEmail,
      tag: filters.tag,
    })
      .then((playerResponse) => {
        if (playerResponse.success) {
          setPlayerReportData(parsePlayerReportApiData(playerResponse.value))
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }, [
    filters.createdAtFrom,
    filters.createdAtUntil,
    filters.gameName,
    filters.playerEmail,
    filters.tag,
    reportType,
    user,
  ])

  //THIS IS THE SPONSORED REPORT FETCHER
  useEffect(() => {
    if (!user || reportType !== ReportType.SPONSORED) {
      return
    }
    setLoading(true)
    getSponsoredReportData({
      businessId: user.activeBusiness.id,
      fromDate: filters.createdAtFrom,
      untilDate: filters.createdAtUntil,
    })
      .then((sponsoredResponse) => {
        if (sponsoredResponse.success) {
          setSponsoredReportData(parseSponsoredReportData(sponsoredResponse.value))
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }, [filters.createdAtFrom, filters.createdAtUntil, reportType, user])

  const fetchSponsoredGameData = useCallback(
    async (gameId: number) => {
      if (!user || reportType !== ReportType.SPONSORED) {
        return undefined
      }
      setLoading(true)
      if (!initialFetchDone) {
        setPageData(undefined)
        setSponsoredDailyPlayers(undefined)
      }
      setActiveSponsoredGameId(gameId)
      getSponsoredPlayersPerDay({
        businessId: user.activeBusiness.id,
        gameId: gameId,
      }).then((dailyPlayersResponse) => {
        if (dailyPlayersResponse.success) setSponsoredDailyPlayers(dailyPlayersResponse.value.playersPerDays)
      })
      const sponsoredGameResponse = await getSponsoredReportGameData({
        gameId: gameId,
        businessId: user.activeBusiness.id,
        page: currentPage,
        perPage: pageSize,
        fromDate: filters.createdAtFrom,
        untilDate: filters.createdAtUntil,
        sortBy: sortBy + sortDir,
      })
      setLoading(false)
      setInitialFetchDone(true)
      if (sponsoredGameResponse.success) {
        const gameData = parseSponsoredReportGameData(sponsoredGameResponse.value)
        setPageData(gameData.userDownloads)
        setLastPage(Math.ceil((gameData.userDownloadsTotalCount ?? 0) / pageSize))
        return gameData
      } else {
        return undefined
      }
    },
    [
      currentPage,
      filters.createdAtFrom,
      filters.createdAtUntil,
      initialFetchDone,
      pageSize,
      reportType,
      sortBy,
      sortDir,
      user,
    ],
  )

  useEffect(() => {
    if (!user || reportType !== ReportType.SPONSORED || !activeSponsoredGameId) {
      return undefined
    }
    fetchSponsoredGameData(activeSponsoredGameId)
  }, [
    currentPage,
    filters.createdAtFrom,
    filters.createdAtUntil,
    pageSize,
    activeSponsoredGameId,
    user,
    reportType,
    fetchSponsoredGameData,
  ])

  const fetchCompareGamesData = useCallback(
    (gameIds: number[]) => {
      if (!user || reportType !== ReportType.INSTRUCTOR) {
        return
      }
      setCompareGameData(undefined)
      getCompareGamesData({ businessId: user?.activeBusiness.id, gameIds }).then((compareDataResponse) => {
        if (compareDataResponse.success) {
          setCompareGameData(parseCompareGameApiData(compareDataResponse.value))
        }
      })
    },
    [reportType, user],
  )

  useEffect(() => {
    const calculateAllPageOptions = Array.from({ length: lastPage }, (_, i) => ({
      value: i + 1,
      label: `${i + 1}`,
    }))
    setAllPageOptions(calculateAllPageOptions)
  }, [lastPage])

  const refreshData = useCallback(() => {
    setRefreshCounter((prev) => prev + 1)
  }, [])

  const setSort = useCallback((sortBy: ReportSortBy, sortDir: ReportSortDir) => {
    setSortBy(sortBy)
    setSortDir(sortDir)
    setCurrentPage(1)
  }, [])

  return (
    <ReporsDataContext.Provider
      value={{
        playerReportData,
        orgSummaryData,
        orgDailyPlayers,
        instructorReportSummaryData,
        compareGameData,
        sponsoredReportData,
        sponsoredDailyPlayers,
        currentPage,
        pageSize,
        pageData,
        lastPage,
        allPageOptions,
        loading,
        initialFetchDone,
        filters,
        sortBy,
        sortDir,
        setPage,
        selectPageSize,
        next,
        prev,
        refreshData,
        setFilters,
        setSort,
        fetchCompareGamesData,
        fetchSponsoredGameData,
      }}
    >
      {children}
    </ReporsDataContext.Provider>
  )
}

export const useReportData = () => {
  const context = useContext(ReporsDataContext)

  return context
}
